import { toast } from "react-toastify";
import { ApiError, AuthStatus, LoginRequest, LogoutResponse } from "../models/auth";
import { EditableNotificationItem, NotificationChannel, NotificationRequest, NotificationRequestV2, NotifyResult, PaginatedEditableNotificationsResponse } from "../models/notification";
import { TelegramProxyLink, TelegramProxyLinkRequest } from "../models/proxy";
import { OnlineUsersStatsPoint, ServerStatsResponse } from "../models/stats";
import { PaginatedSyncLinksResponse, SyncFilter, SyncLinksPageParams } from "../models/sync";
import { AdminUserInfoDetailsDto, PaginatedUsersResponse, UsersPageParams } from "../models/user";
import { XuiClient, XuiClientsResponse, XuiHostRequest, XuiServerConfig } from "../models/xui";
import { CookieManager } from "../utils/cookies";
import { CacheStatsResponse, CookieOptions, IApiClient, ResultResponse } from "./IApiClient";

export class ApiClient implements IApiClient {
	readonly baseUrl: string;
	readonly authCookieName: string;
	onUnauthorized?: () => void;

	constructor(baseUrl: string, authCookieName: string = "auth_token") {
		this.baseUrl = baseUrl;
		this.authCookieName = authCookieName;
	}

	buildUrl = (path: string): string => {
		return `${this.baseUrl}${path}`;
	};

	/**
	 * Авторизация пользователя
	 */
	async login(credentials: LoginRequest): Promise<AuthStatus> {
		const formData = new URLSearchParams();
		formData.append("username", credentials.username);
		formData.append("password", credentials.password);

		await this.fetcher<void>({
			input: this.buildUrl("/auth/login"),
			init: {
				method: "POST",
				body: formData.toString(),
				headers: {
					"Content-Type": "application/x-www-form-urlencoded"
				}
			},
			mapToastMsg: (msg) => `Ошибка авторизации: ${msg}`,
		});

		return { success: true, result: { username: credentials.username } }
	}

	async sendNotification(
		request: NotificationRequest,
		files?: File[],
	): Promise<NotifyResult> {
		const formData = new FormData();
		formData.append(
			"payload",
			new Blob([JSON.stringify(request)], { type: "application/json" }),
		);

		files?.forEach((file) => {
			formData.append("files", file);
		});

		return await this.fetcher<NotifyResult>({
			input: this.buildUrl("/notifications"),
			init: {
				method: "POST",
				body: formData,
			},
		});
	}

	async sendNotificationV2(
		request: NotificationRequestV2,
		files?: File[],
	): Promise<NotifyResult> {
		const { channel, sendToAll, filter, telegram } = request;
		const entry = {
			userFilter: {
				...filter,
				sendToAll,
			},
			payload: telegram,
			channels: [channel],
		};
		const formData = new FormData();
		formData.append(
			"payload",
			new Blob([JSON.stringify(entry)], { type: "application/json" }),
		);

		files?.forEach((file) => {
			formData.append("files", file);
		});

		return await this.fetcher<NotifyResult>({
			input: this.buildUrl("/notifications/v2"),
			init: {
				method: "POST",
				body: formData,
			},
		});
	}

	async listNotifications(params: { page?: number; size?: number } = {}): Promise<PaginatedEditableNotificationsResponse> {
		const { page = 0, size = 20 } = params;
		const query = new URLSearchParams({
			page: String(page),
			size: String(size),
		});
		return await this.fetcher<PaginatedEditableNotificationsResponse>({
			input: `${this.buildUrl("/notifications/list")}?${query.toString()}`,
			init: {
				method: "GET",
			},
		});
	}

	async editNotification(
		notificationId: string,
		payload: any,
		channels: NotificationChannel[] = [],
	): Promise<ResultResponse<void>> {
		return await this.fetcher<ResultResponse<void>>({
			input: this.buildUrl("/notifications/edit"),
			init: {
				method: "POST",
				body: JSON.stringify({
					notificationId,
					payload,
					channels,
				}),
				headers: {
					"Content-Type": "application/json",
				},
			},
		});
	}

	/**
	 * Выход из системы
	 */
	async logout(): Promise<LogoutResponse> {
		const response = await this.fetcher<LogoutResponse>({
			input: this.buildUrl("/auth/logout"),
			init: {
				method: "POST",
			},
			mapToastMsg: (msg) => `Ошибка выхода: ${msg}`,
		});

		// Удаляем куки авторизации
		CookieManager.remove(this.authCookieName);

		return response;
	}

	async getUsersPaginated(
		params: UsersPageParams = {},
	): Promise<PaginatedUsersResponse> {
		const { page = 0, pageSize = 20, filter } = params;

		const queryParams = new URLSearchParams({
			page: page.toString(),
			size: pageSize.toString(),
		});

		const body = JSON.stringify(filter);

		return await this.fetcher<PaginatedUsersResponse>({
			input: `${this.buildUrl("/user/list")}?${queryParams.toString()}`,

			init: {
				method: "POST",
				body,
				headers: {
					"Content-Type": "application/json",
				},
			},
		});
	}

	async getUserDetails(userId: string): Promise<AdminUserInfoDetailsDto> {
		return await this.fetcher<AdminUserInfoDetailsDto>({
			input: `${this.buildUrl("/user?id=")}${userId}`,
			init: {
				method: "GET",
			},
		});
	}

	/**
	 * Продлить подписку пользователя
	 */
	async updateUserSub(
		userId: string,
		params: { type?: string; end?: string; durationDays?: number },
		request?: any,
	): Promise<any> {
		const { type = "GIFTED", end, durationDays } = params;

		const query = new URLSearchParams();
		query.append("type", type);
		if (end) query.append("end", end);
		if (typeof durationDays === "number")
			query.append("durationDays", durationDays.toString());

		return await this.fetcher<any>({
			input: `${this.buildUrl(`/user/${userId}/update_sub`)}?${query.toString()}`,
			init: {
				method: "POST",
				body: request ? JSON.stringify(request) : undefined,
				headers: request ? { "Content-Type": "application/json" } : undefined,
			},
		});
	}

	async getXuiClients(params?: { flow?: string }): Promise<XuiClientsResponse> {
		const query = new URLSearchParams();
		if (params?.flow) query.append("flow", params.flow);

		const qs = query.toString();

		return await this.fetcher<XuiClientsResponse>({
			input: qs ? `${this.buildUrl("/xui-users")}?${qs}` : this.buildUrl("/xui-users"),
			init: {
				method: "GET",
			}
		});
	}

	async getXuiServers(): Promise<XuiServerConfig[]> {
		return await this.fetcher<XuiServerConfig[]>({
			input: this.buildUrl("/xui/servers"),
			init: {
				method: "GET",
			},
		});
	}

	// === XUI Hosts Management (new API) ===
	async getXuiHosts(): Promise<XuiServerConfig[]> {
		return await this.fetcher<XuiServerConfig[]>({
			input: this.buildUrl("/xui/hosts"),
			init: {
				method: "GET",
			},
		});
	}

	async getXuiHost(identity: string): Promise<XuiServerConfig> {
		return await this.fetcher<XuiServerConfig>({
			input: this.buildUrl(`/xui/hosts/${identity}`),
			init: {
				method: "GET",
			},
		});
	}

	async getHostLatency(identity: string): Promise<{ result: number; success: boolean; error?: string }> {
		return await this.fetcher<{ result: number; success: boolean; error?: string }>({
			input: this.buildUrl(`/xui/hosts/latency/${identity}`),
			init: {
				method: "GET",
			},
		});
	}

	async getSupportedHostTypes(): Promise<string[]> {
		return await this.fetcher<string[]>({
			input: this.buildUrl("/xui/hosts/supported-types"),
			init: {
				method: "GET",
			},
		});
	}

	async registerXuiHost(host: XuiHostRequest): Promise<void> {
		return await this.fetcher<void>({
			input: this.buildUrl("/xui/hosts"),
			init: {
				method: "POST",
				body: JSON.stringify(host),
				headers: {
					"Content-Type": "application/json",
				},
			},
		});
	}

	async activateXuiHost(identity: string): Promise<void> {
		return await this.fetcher<void>({
			input: this.buildUrl(`/xui/hosts/${identity}/activate`),
			init: {
				method: "POST",
			},
		});
	}

	async deactivateXuiHost(identity: string): Promise<void> {
		return await this.fetcher<void>({
			input: this.buildUrl(`/xui/hosts/${identity}/deactivate`),
			init: {
				method: "POST",
			},
		});
	}

	async updateXuiHost(identity: string, host: XuiHostRequest): Promise<void> {
		return await this.fetcher<void>({
			input: this.buildUrl(`/xui/hosts/${identity}`),
			init: {
				method: "POST",
				body: JSON.stringify(host),
				headers: {
					"Content-Type": "application/json",
				},
			},
		});
	}

	async deleteXuiHost(identity: string): Promise<void> {
		return await this.fetcher<void>({
			input: this.buildUrl(`/xui/hosts/${identity}`),
			init: {
				method: "DELETE",
			},
		});
	}

	async addXuiClients(
		clients: XuiClient[],
		xuiServerIdentity?: string,
	): Promise<ResultResponse<void>> {
		const query = new URLSearchParams();
		if (xuiServerIdentity) {
			query.append("identity", xuiServerIdentity);
		}
		const qs = query.toString();
		const url = qs
			? `${this.buildUrl("/xui/add-clients")}?${qs}`
			: this.buildUrl("/xui/add-clients");

		return await this.fetcher<ResultResponse<void>>({
			input: url,
			init: {
				method: "POST",
				body: JSON.stringify(clients),
				headers: {
					"Content-Type": "application/json",
				},
			},
		});
	}

	async updateXuiGeofile(xuiServerIdentity?: string): Promise<ResultResponse<void>> {
		const query = new URLSearchParams();
		if (xuiServerIdentity) {
			query.append("identity", xuiServerIdentity);
		}
		const qs = query.toString();
		const url = qs
			? `${this.buildUrl("/xui/update-geofile")}?${qs}`
			: this.buildUrl("/xui/update-geofile");

		return await this.fetcher<ResultResponse<void>>({
			input: url,
			init: {
				method: "POST",
			},
		});
	}

	/**
	 * Проверка статуса авторизации
	 */
	async checkAuthStatus(): Promise<AuthStatus> {
		return await this.fetcher<AuthStatus>({
			input: this.buildUrl("/auth/check"),
			init: {
				method: "GET",
			}
		});
	}

	/**
	 * Универсальный GET запрос
	 */
	async get<T>(path: string, params?: Record<string, string>): Promise<T> {
		let url = this.buildUrl(path);

		if (params) {
			const searchParams = new URLSearchParams(params);
			url += `?${searchParams.toString()}`;
		}

		return await this.fetcher<T>({
			input: url,
			init: {
				method: "GET",
			},
		});
	}

	/**
	 * Универсальный POST запрос
	 */
	async post<T>(path: string, data?: any): Promise<T> {
		return await this.fetcher<T>({
			input: this.buildUrl(path),
			init: {
				method: "POST",
				body: data ? JSON.stringify(data) : undefined,
			},
		});
	}

	/**
	 * Универсальный PUT запрос
	 */
	async put<T>(path: string, data?: any): Promise<T> {
		return await this.fetcher<T>({
			input: this.buildUrl(path),
			init: {
				method: "PUT",
				body: data ? JSON.stringify(data) : undefined,
			},
		});
	}

	/**
	 * Универсальный PATCH запрос
	 */
	async patch<T>(path: string, data?: any): Promise<T> {
		return await this.fetcher<T>({
			input: this.buildUrl(path),
			init: {
				method: "PATCH",
				body: data ? JSON.stringify(data) : undefined,
			},
		});
	}

	/**
	 * Универсальный DELETE запрос
	 */
	async delete<T>(path: string): Promise<T> {
		return await this.fetcher<T>({
			input: this.buildUrl(path),
			init: {
				method: "DELETE",
			},
		});
	}

	/**
	 * POST multipart/form-data c произвольным FormData
	 */
	async postForm<T>(path: string, formData: FormData): Promise<T> {
		return await this.fetcher<T>({
			input: this.buildUrl(path),
			init: {
				method: "POST",
				body: formData,
			},
		});
	}

	/**
	 * Загрузка файла
	 */
	async uploadFile<T>(
		path: string,
		file: File,
		additionalData?: Record<string, any>,
	): Promise<T> {
		const formData = new FormData();
		formData.append("file", file);

		if (additionalData) {
			Object.entries(additionalData).forEach(([key, value]) => {
				formData.append(key, value);
			});
		}

		return await this.fetcher<T>({
			input: this.buildUrl(path),
			init: {
				method: "POST",
				body: formData,
				headers: {
					// Не устанавливаем Content-Type для FormData, браузер сам установит
				},
			},
		});
	}

	/**
	 * Получение списка синхронизаций
	 */
	async listSyncLinks(
		params: SyncLinksPageParams = {},
	): Promise<PaginatedSyncLinksResponse> {
		const { page = 0, pageSize = 20, status } = params;
		const queryParams = new URLSearchParams({
			page: page.toString(),
			size: pageSize.toString(),
			status: status || ''
		});

		return await this.fetcher<PaginatedSyncLinksResponse>({
			input: `${this.buildUrl("/sync")}?${queryParams.toString()}`,
			init: {
				method: "GET",
			},
		});
	}

	/**
	 * Получить статистику серверов, сгруппированную по hostId.
	 * step — шаг по шкале X в ISO-8601 duration (PT30M, PT1H, P7D, P30D).
	 * from — начало интервала в ISO date-time; по умолчанию backend — последние сутки.
	 * hosts — набор идентификаторов хостов для фильтрации; если не передан — все хосты.
	 */
	async getServerStats(params?: {
		step?: string;
		from?: string;
		agg: "MAX" | "AVG";
		hosts: string[];
	}): Promise<ServerStatsResponse> {
		const query = new URLSearchParams();
		if (params?.step) {
			query.append("step", params.step);
		}
		if (params?.from) {
			query.append("from", params.from);
		}
		if (params?.agg) {
			query.append("agg", params.agg);
		}
		if (params?.hosts && params.hosts.length > 0) {
			params.hosts.forEach((host) => query.append("hosts", host));
		}

		const qs = query.toString();
		const url = `${this.buildUrl("/sys/stats")}?${qs}`

		return await this.fetcher<ServerStatsResponse>({
			input: url,
			init: {
				method: "GET",
			},
		});
	}

	/**
	 * Статистика онлайн-пользователей за период (шаг — день).
	 * from — начало интервала в ISO date-time; по умолчанию бэкенд — последние сутки.
	 */
	async getOnlineUserStats(params?: {
		from?: string;
	}): Promise<OnlineUsersStatsPoint[]> {
		const query = new URLSearchParams();
		if (params?.from) {
			query.append("from", params.from);
		}

		const qs = query.toString();
		const url = qs
			? `${this.buildUrl("/sys/user-stats")}?${qs}`
			: this.buildUrl("/sys/user-stats");

		return await this.fetcher<OnlineUsersStatsPoint[]>({
			input: url,
			init: {
				method: "GET",
			},
		});
	}

	/**
	 * Запуск синхронизации ошибок
	 */
	async syncErrors(filter: SyncFilter): Promise<void> {
		return await this.fetcher<void>({
			input: this.buildUrl("/sync"),
			init: {
				method: "POST",
				body: JSON.stringify(filter),
				headers: {
					"Content-Type": "application/json",
				},
			},
		});
	}

	async getTelegramProxyLinks(): Promise<TelegramProxyLink[]> {
		return await this.fetcher<TelegramProxyLink[]>({
			input: this.buildUrl("/telegram-proxy-links"),
			init: {
				method: "GET",
			},
		});
	}

	async createTelegramProxyLink(request: TelegramProxyLinkRequest): Promise<TelegramProxyLink> {
		return await this.fetcher<TelegramProxyLink>({
			input: this.buildUrl("/telegram-proxy-links"),
			init: {
				method: "POST",
				body: JSON.stringify(request),
				headers: {
					"Content-Type": "application/json",
				},
			},
		});
	}

	async updateTelegramProxyLink(
		id: number,
		request: TelegramProxyLinkRequest,
	): Promise<TelegramProxyLink> {
		return await this.fetcher<TelegramProxyLink>({
			input: this.buildUrl(`/telegram-proxy-links/${id}`),
			init: {
				method: "POST",
				body: JSON.stringify(request),
				headers: {
					"Content-Type": "application/json",
				},
			},
		});
	}

	async listCaches(): Promise<string[]> {
		return await this.fetcher<string[]>({
			input: this.buildUrl("/cache"),
			init: { method: "GET" },
		});
	}

	async getCacheStats(): Promise<CacheStatsResponse> {
		return await this.fetcher<CacheStatsResponse>({
			input: this.buildUrl("/cache/stats"),
			init: { method: "GET" },
		});
	}

	async flushAllCaches(): Promise<ResultResponse<void>> {
		return await this.fetcher<ResultResponse<void>>({
			input: this.buildUrl("/cache"),
			init: { method: "DELETE" },
		});
	}

	async flushCache(name: string): Promise<ResultResponse<void>> {
		return await this.fetcher<ResultResponse<void>>({
			input: this.buildUrl(`/cache/${name}`),
			init: { method: "DELETE" },
		});
	}

	async deleteTelegramProxyLink(id: number): Promise<ResultResponse<void>> {
		return await this.fetcher<ResultResponse<void>>({
			input: this.buildUrl(`/telegram-proxy-links/${id}`),
			init: {
				method: "DELETE",
			},
		});
	}

	/**
	 * Проверка, авторизован ли пользователь локально
	 */
	isAuthenticated(): boolean {
		return CookieManager.has(this.authCookieName);
	}

	/**
	 * Получить токен авторизации из куки
	 */
	getAuthToken(): string | null {
		return CookieManager.get(this.authCookieName);
	}

	/**
	 * Установить токен авторизации в куки
	 */
	setAuthToken(token: string, options?: CookieOptions): void {
		CookieManager.set(this.authCookieName, token, options);
	}

	/**
	 * Очистить токен авторизации
	 */
	clearAuthToken(): void {
		CookieManager.remove(this.authCookieName);
	}

	headerProvider: () => Record<string, string> = () => ({});

	setHeaderProvider(fn: () => Record<string, string>) {
		this.headerProvider = fn;
	}

	async fetcher<T>({
		input,
		init,
		onStatus,
		mapToastMsg
	}: {
		input: RequestInfo
		init?: RequestInit
		onStatus?: (status: number) => void
		mapToastMsg?: (msg: string) => string
	}): Promise<T> {
		const headersObj = new Headers({
			...this.headerProvider(),
			...(init?.headers as HeadersInit | undefined),
		});
		let body = init?.body;
		let contentType = headersObj.get("Content-Type");

		if (contentType === undefined || contentType === null) {
			if (body instanceof FormData) {
				headersObj.delete("Content-Type");
			} else if (body instanceof URLSearchParams) {
				contentType = "application/x-www-form-urlencoded";
				body = body.toString();
			} else if (body && typeof body === "object" && !(body instanceof Blob)) {
				contentType = "application/json";
				body = JSON.stringify(body);
			} else if (body) {
				contentType = "application/json";
			}
		} else if (
			contentType.includes("application/json") &&
			body &&
			typeof body === "object" &&
			!(body instanceof Blob) &&
			!(body instanceof FormData) &&
			!(body instanceof URLSearchParams)
		) {
			body = JSON.stringify(body);
		} else if (
			contentType.includes("application/x-www-form-urlencoded") &&
			body instanceof URLSearchParams
		) {
			body = body.toString();
		}

		if (contentType) {
			headersObj.set("Content-Type", contentType);
		}

		const fetchOptions: RequestInit = {
			...init,
			headers: headersObj,
			body,
			credentials: 'include',
		};

		const response = await fetch(input, fetchOptions);

		if (!response.ok) {
			onStatus?.(response.status);

			let errorMessage = `API Error: ${response.status}`;

			try {
				const errorData = await response.json();
				errorMessage = errorData.message || errorData.error || errorMessage;
			} catch {
				// Если не удалось распарсить JSON, используем стандартное сообщение
			}

			const error = new Error(errorMessage) as unknown as ApiError;
			error.status = response.status;

			// Обработка различных статус кодов
			switch (response.status) {
				case 401:
					toast.error('Необходима авторизация');
					CookieManager.remove(this.authCookieName);
					this.onUnauthorized?.();
					break;
				case 403:
					toast.error('Доступ запрещен');
					break;
				case 404:
					toast.error('Ресурс не найден');
					break;
				case 500:
					toast.error('Внутренняя ошибка сервера');
					break;
				case 504:
					toast.error('Сервер не отвечает. Попробуйте позже');
					break;
				default:
					toast.error(mapToastMsg?.(errorMessage) || errorMessage);
			}

			throw error;
		}

		return response.headers.get('content-type')?.includes('application/json')
			? await response.json()
			: {} as T;
	}
}
